import { useState, useEffect } from 'react';
import { 
  getDefaultPermissionsForRole, 
  hasPermission, 
  validatePermissions 
} from '../utils/permissionUtils';
import api from '../utils/api/permissionApi';
import { isClientRole } from '../auth/roleUtils';

/**
 * Custom hook for managing user permissions with backend integration
 */
export const usePermissions = (userId = null, userRole = null, userPermissions = null, organizationReferenceId = null) => {
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const refresh = () => setRevision(value => value + 1);
    window.addEventListener('vela:permissions-updated', refresh);
    return () => window.removeEventListener('vela:permissions-updated', refresh);
  }, []);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    // Initialize permissions from the active organization.
    const initializePermissions = async () => {
      setLoading(true);
      
      let finalPermissions = [];
      
      try {
        if (isClientRole(userRole)) {
          finalPermissions = [];
        } else if (userId) {
          // Try to fetch actual permissions from backend
          // Ensure userId is a string for backend API
          const userIdString = userId.toString();
          const backendPermissions = await api.fetchUserPermissions(userIdString, organizationReferenceId);
          finalPermissions = backendPermissions.map(p => p.name);
        } else if (userPermissions && validatePermissions(userPermissions)) {
          // Use explicit permissions if provided and valid
          finalPermissions = userPermissions;
        } else if (userRole) {
          // Fall back to default role permissions
          finalPermissions = getDefaultPermissionsForRole(userRole);
        }
      } catch (error) {
        if (error?.status !== 409 || error?.code !== 'MEMBERSHIP_MISSING') {
          console.error('Error fetching user permissions:', error);
        }
        // Failed authenticated checks must not resurrect role defaults.
        finalPermissions = [];
      }
      
      if (cancelled) return;
      setPermissions(finalPermissions);
      setLoading(false);
    };

    initializePermissions();
    return () => { cancelled = true; };
  }, [userId, userRole, userPermissions, organizationReferenceId, revision]);

  /**
   * Check if user has specific permission
   */
  const can = (permissionName) => {
    return hasPermission(permissions, permissionName);
  };

  /**
   * Check if user has any of the specified permissions
   */
  const canAny = (permissionNames) => {
    return Array.isArray(permissionNames) && 
           permissionNames.some(permName => hasPermission(permissions, permName));
  };

  /**
   * Check if user has all of the specified permissions
   */
  const canAll = (permissionNames) => {
    return Array.isArray(permissionNames) && 
           permissionNames.every(permName => hasPermission(permissions, permName));
  };

  /**
   * Update user permissions
   */
  const updatePermissions = (newPermissions) => {
    if (validatePermissions(newPermissions)) {
      setPermissions(newPermissions);
      return true;
    }
    return false;
  };

  return {
    permissions,
    loading,
    can,
    canAny,
    canAll,
    updatePermissions
  };
};

/**
 * Hook for managing organization permissions
 */
export const useOrganizationPermissions = () => {
  const [userPermissions, setUserPermissions] = useState(new Map());
  const [loading, setLoading] = useState(false);

  /**
   * Load permissions for multiple users
   */
  const loadUsersPermissions = async (users) => {
    setLoading(true);
    
    try {
      const permissionMap = new Map();
      
      users.forEach(user => {
        const permissions = user.permissions || getDefaultPermissionsForRole(user.role);
        permissionMap.set(user.id, permissions);
      });
      
      setUserPermissions(permissionMap);
    } catch (error) {
      console.error('Error loading user permissions:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Update permissions for a specific user
   */
  const updateUserPermissions = (userId, newPermissions) => {
    if (validatePermissions(newPermissions)) {
      setUserPermissions(prev => {
        const updated = new Map(prev);
        updated.set(userId, newPermissions);
        return updated;
      });
      return true;
    }
    return false;
  };

  /**
   * Get permissions for a specific user
   */
  const getUserPermissions = (userId) => {
    return userPermissions.get(userId) || [];
  };

  /**
   * Check if a user has a specific permission
   */
  const userCan = (userId, permissionId) => {
    const permissions = getUserPermissions(userId);
    return hasPermission(permissions, permissionId);
  };

  return {
    userPermissions,
    loading,
    loadUsersPermissions,
    updateUserPermissions,
    getUserPermissions,
    userCan
  };
};
